import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { t } from '../../lib/i18n';
import { getInitials, cn } from '../../lib/utils';
import axios from 'axios';
import { Input } from '../ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Search, Loader2, X, User, FileText, ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GlobalSearchBar({ className, onClose }) {
  const { token, language } = useAuth();
  const tr = t(language);
  const navigate = useNavigate();
  
  const [query, setQuery] = useState('');
  const [results, setResults] = useState({ users: [], posts: [] });
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = useCallback(async (value) => {
    setQuery(value);
    
    if (value.length < 2) {
      setResults({ users: [], posts: [] });
      setShowResults(false);
      return;
    }

    // Debounce search
    if (debounceRef.current) clearTimeout(debounceRef.current);
    
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      setShowResults(true);
      
      try {
        const response = await axios.get(`${API}/search?q=${encodeURIComponent(value)}&limit=5`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setResults(response.data);
      } catch (error) {
        console.error('Search failed:', error);
      } finally {
        setLoading(false);
      }
    }, 300);
  }, [token]);

  const handleNavigate = (path) => {
    setShowResults(false);
    setQuery('');
    navigate(path);
    onClose?.();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setShowResults(false);
      onClose?.();
    } else if (e.key === 'Enter' && query.length >= 2) {
      handleNavigate(`/search?q=${encodeURIComponent(query)}`);
    }
  };

  const hasResults = results.users.length > 0 || results.posts.length > 0;

  return (
    <div ref={containerRef} className={cn("relative w-full max-w-lg", className)} data-testid="global-search">
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground pointer-events-none" />
        <Input
          ref={inputRef}
          type="text"
          placeholder={tr('search') || 'Search users, posts...'}
          value={query}
          onChange={(e) => handleSearch(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => query.length >= 2 && setShowResults(true)}
          className="pl-12 pr-10 h-12 rounded-full bg-secondary/50 border-border/50 focus:border-primary/50 focus:ring-primary/20"
          data-testid="global-search-input"
        />
        {query && (
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full hover:bg-secondary"
            onClick={() => {
              setQuery('');
              setResults({ users: [], posts: [] });
              setShowResults(false);
              inputRef.current?.focus();
            }}
          >
            <X className="w-4 h-4" />
          </Button>
        )}
      </div>

      {/* Results Dropdown */}
      {showResults && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-background/95 backdrop-blur-xl border border-border/50 rounded-2xl shadow-2xl shadow-black/20 overflow-hidden z-[100] max-h-[70vh] overflow-y-auto">
          {loading && (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          )}

          {!loading && query.length >= 2 && !hasResults && (
            <div className="flex flex-col items-center justify-center py-12 px-4">
              <Search className="w-12 h-12 text-muted-foreground/50 mb-3" />
              <p className="text-muted-foreground text-center">{tr('noResults') || 'No results found'}</p>
              <p className="text-xs text-muted-foreground/70 mt-1">Try a different search term</p>
            </div>
          )}

          {!loading && hasResults && (
            <>
              {/* Users Section */}
              {results.users.length > 0 && (
                <div>
                  <div className="px-4 py-2 bg-secondary/30 border-b border-border/30">
                    <p className="text-xs font-semibold text-muted-foreground uppercase flex items-center gap-2">
                      <User className="w-3 h-3" />
                      Users
                    </p>
                  </div>
                  {results.users.map((user) => (
                    <button
                      key={user.id}
                      onClick={() => handleNavigate(`/profile/${user.username}`)}
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-secondary/50 transition-colors"
                      data-testid={`search-user-${user.id}`}
                    >
                      <Avatar className="w-10 h-10 ring-2 ring-primary/10">
                        <AvatarImage src={user.avatar} alt={user.display_name} />
                        <AvatarFallback className="bg-primary/10 text-primary text-sm">
                          {getInitials(user.display_name || user.username)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 text-left min-w-0">
                        <p className="font-semibold truncate">{user.display_name || user.username}</p>
                        <p className="text-sm text-muted-foreground truncate">@{user.username}</p>
                      </div>
                      <ArrowRight className="w-4 h-4 text-muted-foreground" />
                    </button>
                  ))}
                </div>
              )}

              {/* Posts Section */}
              {results.posts.length > 0 && (
                <div>
                  <div className="px-4 py-2 bg-secondary/30 border-b border-border/30">
                    <p className="text-xs font-semibold text-muted-foreground uppercase flex items-center gap-2">
                      <FileText className="w-3 h-3" />
                      Posts
                    </p>
                  </div>
                  {results.posts.map((post) => (
                    <button
                      key={post.id}
                      onClick={() => handleNavigate(`/post/${post.id}`)}
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-secondary/50 transition-colors"
                      data-testid={`search-post-${post.id}`}
                    >
                      <Avatar className="w-10 h-10">
                        <AvatarImage src={post.avatar} alt={post.username} />
                        <AvatarFallback className="bg-primary/10 text-primary text-sm">
                          {getInitials(post.display_name || post.username)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 text-left min-w-0">
                        <p className="font-medium text-sm truncate">@{post.username}</p>
                        <p className="text-sm text-muted-foreground truncate line-clamp-1">
                          {post.content.substring(0, 80)}{post.content.length > 80 ? '...' : ''}
                        </p>
                      </div>
                      <ArrowRight className="w-4 h-4 text-muted-foreground" />
                    </button>
                  ))}
                </div>
              )}

              {/* See All Results */}
              <button
                onClick={() => handleNavigate(`/search?q=${encodeURIComponent(query)}`)}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary/5 hover:bg-primary/10 text-primary font-medium transition-colors border-t border-border/30"
              >
                <Search className="w-4 h-4" />
                See all results for "{query}"
                <ArrowRight className="w-4 h-4" />
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
